import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { clientDashboardKeys } from './clientDashboards'
import { trackerKeys } from './keys'
import { extractErrorMessage } from './ticketMutations'
import type { CallRequest, ClientDomain, ClientMeeting, ClientMeetings, LinkMismatches, LinkTargets, NotLoggedEntry, RemovedMeeting, RequestLinkKind, WaitingMeeting } from '@/types/clientMeetings'

export const clientMeetingKeys = {
  meetings: (companyId: string) => [...clientDashboardKeys.all, 'meetings', companyId] as const,
  unplaced: () => [...clientDashboardKeys.all, 'meetings-unplaced'] as const,
  removedAll: () => [...clientDashboardKeys.all, 'meetings-removed'] as const,
  domains: (companyId: string) => [...clientDashboardKeys.all, 'email-domains', companyId] as const,
  mismatches: () => [...clientDashboardKeys.all, 'link-mismatches'] as const,
  notLogged: (companyId: string, dismissed: boolean) => [...clientDashboardKeys.all, 'not-logged', companyId, dismissed] as const,
  linkTargets: (companyId: string) => [...clientDashboardKeys.all, 'link-targets', companyId] as const,
}

export function useClientMeetings(companyId: string) {
  return useQuery({
    queryKey: clientMeetingKeys.meetings(companyId),
    queryFn: () => api<{ data: ClientMeetings }>(`/client-dashboards/${companyId}/meetings`),
  })
}

// Check Fireflies now; the response replaces the tab's data.
export function useFetchClientMeetings(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ data: ClientMeetings }>(`/client-dashboards/${companyId}/meetings/fetch`, { method: 'POST' }),
    onSuccess: data => {
      queryClient.setQueryData(clientMeetingKeys.meetings(companyId), data)
      if (data.data.checked === false) toast.info('A Fireflies check is already running. Try again in a minute.')
      else toast.success('Up to date with Fireflies')
      return queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all, predicate: query => query.queryKey[1] !== 'meetings' })
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't reach Fireflies")),
  })
}

export function useUnplacedMeetings() {
  return useQuery({
    queryKey: clientMeetingKeys.unplaced(),
    queryFn: () => api<{ data: WaitingMeeting[] }>('/client-dashboards/meetings/unplaced'),
  })
}

// Refresh only what a meeting change can affect: meeting lists, last call on the dashboards, client list, prep and
// KPIs, and the call's requests on Not logged and Linear Tickets.
const MEETING_AFFECTED = ['meetings', 'meetings-unplaced', 'meetings-removed', 'list', 'detail', 'not-logged', 'linear-issues', 'emails', 'prep', 'kpis']
const invalidateMeetingViews = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all, predicate: query => MEETING_AFFECTED.includes(String(query.queryKey[1])) })

function useMeetingAction<TVariables>(path: (variables: TVariables) => string, body: (variables: TVariables) => Record<string, string> | undefined, success: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: TVariables) => api<{ data?: ClientMeeting }>(path(variables), { method: 'POST', body: body(variables) }),
    onSuccess: () => {
      toast.success(success)
      return invalidateMeetingViews(queryClient)
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't update the meeting")),
  })
}

type Placement = { meetingId: string; companyId: string }

export const useAssignMeeting = () =>
  useMeetingAction<Placement>(({ meetingId }) => `/client-dashboards/meetings/${meetingId}/assign`, ({ companyId }) => ({ companyId }), 'Meeting assigned')

export const useMoveMeeting = () =>
  useMeetingAction<Placement>(({ meetingId }) => `/client-dashboards/meetings/${meetingId}/move`, ({ companyId }) => ({ companyId }), 'Meeting moved')

export const useRemoveMeeting = () =>
  useMeetingAction<{ meetingId: string }>(({ meetingId }) => `/client-dashboards/meetings/${meetingId}/remove`, () => undefined, 'Meeting removed from the hub')

// Refresh Removed meetings whatever the outcome: a meeting Fireflies no longer has is cleared from the list on failure.
export function useRestoreMeeting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId }: { meetingId: string }) => api(`/client-dashboards/meetings/${meetingId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Meeting restored')
      return invalidateMeetingViews(queryClient)
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't restore the meeting")),
    onSettled: () => queryClient.invalidateQueries({ queryKey: clientMeetingKeys.removedAll() }),
  })
}

export const REMOVED_PAGE_SIZE = 20

export function useRemovedMeetings() {
  return useInfiniteQuery({
    queryKey: clientMeetingKeys.removedAll(),
    queryFn: ({ pageParam }) =>
      api<{ data: RemovedMeeting[] }>('/client-dashboards/meetings/removed', {
        query: { limit: String(REMOVED_PAGE_SIZE), ...(pageParam ? { before: pageParam } : {}) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => (lastPage.data.length === REMOVED_PAGE_SIZE ? lastPage.data[lastPage.data.length - 1].removedAt : null),
  })
}

export function useClientDomains(companyId: string) {
  return useQuery({
    queryKey: clientMeetingKeys.domains(companyId),
    queryFn: () => api<{ data: ClientDomain[] }>(`/client-dashboards/${companyId}/email-domains`),
  })
}

export function useAddClientDomain(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) => api<{ data: ClientDomain[] }>(`/client-dashboards/${companyId}/email-domains`, { method: 'POST', body: { domain } }),
    onSuccess: data => queryClient.setQueryData(clientMeetingKeys.domains(companyId), data),
  })
}

export function useRemoveClientDomain(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) =>
      api<{ data: ClientDomain[] }>(`/client-dashboards/${companyId}/email-domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
    onSuccess: data => queryClient.setQueryData(clientMeetingKeys.domains(companyId), data),
    onError: err => toast.error(extractErrorMessage(err, "Couldn't remove the domain")),
  })
}

export function useLinkMismatches() {
  return useQuery({
    queryKey: clientMeetingKeys.mismatches(),
    queryFn: () => api<{ data: LinkMismatches }>('/linear-customer-links/mismatches'),
  })
}

export function useNotLogged(companyId: string, dismissed: boolean) {
  return useQuery({
    queryKey: clientMeetingKeys.notLogged(companyId, dismissed),
    queryFn: () => api<{ data: NotLoggedEntry[] }>(`/client-dashboards/${companyId}/not-logged`, { query: { dismissed: String(dismissed) } }),
    placeholderData: keepPreviousData,
  })
}

export function useLinkTargets(companyId: string, enabled = true) {
  return useQuery({
    queryKey: clientMeetingKeys.linkTargets(companyId),
    queryFn: () => api<{ data: LinkTargets }>(`/client-dashboards/${companyId}/link-targets`),
    enabled,
  })
}

// A request change can move its call's or email's table, the Not logged tab and where Linear issues came from, and nothing else.
const REQUEST_AFFECTED = ['meetings', 'emails', 'not-logged', 'linear-issues', 'link-targets']
export const invalidateRequestViews = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all, predicate: query => REQUEST_AFFECTED.includes(String(query.queryKey[1])) })

function useRequestAction<TVariables>(
  call: (variables: TVariables) => Promise<{ data: CallRequest; warning?: string | null }>,
  fallback: string,
  success?: string,
  alsoRefresh: readonly unknown[][] = [],
  { quiet = false }: { quiet?: boolean } = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: call,
    onSuccess: response => {
      if (response?.warning) toast.warning(response.warning)
      else if (success) toast.success(success)
      return Promise.all([invalidateRequestViews(queryClient), ...alsoRefresh.map(queryKey => queryClient.invalidateQueries({ queryKey }))])
    },
    // A quiet action leaves the failure to its caller, such as a dialog that shows it inline.
    onError: err => {
      if (!quiet) toast.error(extractErrorMessage(err, fallback))
    },
  })
}

const requestPath = (requestId: string, action: string) => `/client-dashboards/requests/${requestId}/${action}`

export const useLinkRequest = () =>
  useRequestAction(
    ({ requestId, kind, id }: { requestId: string; kind: RequestLinkKind; id: string }) =>
      api<{ data: CallRequest }>(requestPath(requestId, 'link'), { method: 'PUT', body: { kind, id } }),
    "Couldn't link the request"
  )

export const useUnlinkRequest = () =>
  useRequestAction(({ requestId }: { requestId: string }) => api<{ data: CallRequest }>(requestPath(requestId, 'link'), { method: 'DELETE' }), "Couldn't unlink the request")

export const useDismissRequest = () =>
  useRequestAction(({ requestId }: { requestId: string }) => api<{ data: CallRequest }>(requestPath(requestId, 'dismiss'), { method: 'POST' }), "Couldn't dismiss the request")

export const useRestoreRequest = () =>
  useRequestAction(({ requestId }: { requestId: string }) => api<{ data: CallRequest }>(requestPath(requestId, 'restore'), { method: 'POST' }), "Couldn't undo the dismissal")

export interface RequestTrackedItemBody {
  requesterId: string
  subject: string
  description: string
  category: string
  type?: string
  priority?: string
  status?: string
  nextAction?: string
  waitingOn?: string | null
  expectedDate?: string | null
  followerIds?: string[]
  notify: boolean
}

export const useCreateRequestTrackedItem = () =>
  useRequestAction(
    ({ requestId, body }: { requestId: string; body: RequestTrackedItemBody }) =>
      api<{ data: CallRequest }>(requestPath(requestId, 'tracked-item'), { method: 'POST', body: { ...body } }),
    "Couldn't create the tracked item",
    'Tracked item created',
    [[...trackerKeys.all]],
    { quiet: true }
  )

export const useCreateRequestLinearIssue = () =>
  useRequestAction(
    ({ requestId, title, description }: { requestId: string; title: string; description: string }) =>
      api<{ data: CallRequest; warning: string | null }>(requestPath(requestId, 'linear-issue'), { method: 'POST', body: { title, description } }),
    "Couldn't create the Linear issue",
    'Linear issue created'
  )

export function useRetryRequests() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId }: { meetingId: string }) => api(`/client-dashboards/meetings/${meetingId}/requests/retry`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('This call will be read again within 15 minutes')
      return invalidateRequestViews(queryClient)
    },
    onError: err => toast.error(extractErrorMessage(err, "Couldn't read the call again")),
  })
}
